import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import axios from 'axios';
import Swal from 'sweetalert2';
import BackOfficeMenu from '../components/BackOfficeMenu';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import { usePermissions } from '../../utils/usePermissions';
import './WarehouseVisualization3D.css';

const API_URL = (import.meta.env.VITE_API_URL).replace(/\/api\/?$/, '');

const WarehouseVisualization3D = ({ onNavigate, onLogout, currentPage }) => {
    const { hasPermission } = usePermissions();
    const mountRef = useRef(null);
    const sceneRef = useRef(null);
    const cameraRef = useRef(null);
    const rendererRef = useRef(null);
    const controlsRef = useRef(null);
    const palettesRef = useRef([]);
    const positionMarkersRef = useRef([]);
    const selectedHighlightRef = useRef(null);
    const cameraMoveRef = useRef(null);
    const clockRef = useRef(new THREE.Clock());
    const movingPaletteRef = useRef(null);
    const animationFrameRef = useRef(null);

    const [palettes, setPalettes] = useState([]);
    const [selectedPalette, setSelectedPalette] = useState(null);
    const [movingPalette, setMovingPalette] = useState(null);
    const [viewMode, setViewMode] = useState('3D');
    const [showLabels, setShowLabels] = useState(true);
    const [filterStatus, setFilterStatus] = useState('all');
    const [stats, setStats] = useState({ total: 0, active: 0, reserve: 0, archive: 0 });
    const [loading, setLoading] = useState(true);

    // Couleurs par statut - palette du BackOffice
    const statusColors = {
        active: '#2ecc71',    // Vert
        reserve: '#f39c12',   // Orange
        archive: '#95a5a6'    // Gris
    };
    const MAX_PALETTES_PER_SLOT = 3;

    const storageSlots = [
        ...Array.from({ length: 5 }, (_, row) => (
            Array.from({ length: 4 }, (_, col) => ({ x: -5 + col, y: 0, z: -4 + row, zone: 'active' }))
        )).flat(),
        ...Array.from({ length: 5 }, (_, row) => (
            Array.from({ length: 4 }, (_, col) => ({ x: 2 + col, y: 0, z: -4 + row, zone: 'reserve' }))
        )).flat(),
        ...Array.from({ length: 2 }, (_, row) => (
            Array.from({ length: 7 }, (_, col) => ({ x: -3 + col, y: 0, z: 4 + row, zone: 'archive' }))
        )).flat()
    ];

    const positionKey = (position = {}) => {
        const x = Number(position.x || 0);
        const y = Number(position.y || 0);
        const z = Number(position.z || 0);
        return `${x},${y},${z}`;
    };

    const disposeObject = (obj) => {
        if (!obj) return;
        obj.traverse?.((child) => {
            if (child.geometry) child.geometry.dispose();
            if (child.material) {
                if (Array.isArray(child.material)) {
                    child.material.forEach(mat => mat.dispose());
                } else {
                    child.material.dispose();
                }
            }
        });
        if (obj.geometry) obj.geometry.dispose();
        if (obj.material) {
            if (Array.isArray(obj.material)) {
                obj.material.forEach(mat => mat.dispose());
            } else {
                obj.material.dispose();
            }
        }
    };

    const createCanvasTexture = (draw) => {
        const canvas = document.createElement('canvas');
        canvas.width = 512;
        canvas.height = 512;
        const ctx = canvas.getContext('2d');
        draw(ctx, canvas);
        const texture = new THREE.CanvasTexture(canvas);
        texture.wrapS = THREE.RepeatWrapping;
        texture.wrapT = THREE.RepeatWrapping;
        texture.colorSpace = THREE.SRGBColorSpace;
        return texture;
    };

    const getEffectivePalettePosition = (palette, index = 0) => {
        const position = palette?.position || {};
        const isUnset = Number(position.x || 0) === 0 && Number(position.y || 0) === 0 && Number(position.z || 0) === 0;
        if (!isUnset) return position;
        return storageSlots[index % storageSlots.length] || { x: 0, y: 0, z: 0 };
    };

    const createGarageStructure = (scene) => {
        const garageWidth = 96;
        const garageDepth = 72;
        const wallHeight = 13;
        const wallThickness = 1.2;
        const doorWidth = 18;
        const doorHeight = 9;
        const frontZ = garageDepth / 2;
        const backZ = -garageDepth / 2;
        const sideX = garageWidth / 2;
        const wallTexture = createCanvasTexture((ctx, canvas) => {
            ctx.fillStyle = '#31465d';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.strokeStyle = 'rgba(255,255,255,0.08)';
            ctx.lineWidth = 3;
            for (let y = 0; y <= canvas.height; y += 64) {
                ctx.beginPath();
                ctx.moveTo(0, y);
                ctx.lineTo(canvas.width, y);
                ctx.stroke();
            }
            for (let x = 0; x <= canvas.width; x += 128) {
                ctx.beginPath();
                ctx.moveTo(x, 0);
                ctx.lineTo(x, canvas.height);
                ctx.stroke();
            }
        });
        wallTexture.repeat.set(4, 2);

        const wallMaterial = new THREE.MeshStandardMaterial({
            color: 0x34495e,
            map: wallTexture,
            roughness: 0.72,
            metalness: 0.12
        });
        const innerWallMaterial = new THREE.MeshStandardMaterial({
            color: 0x40566f,
            map: wallTexture,
            roughness: 0.8,
            metalness: 0.08
        });
        const trimMaterial = new THREE.MeshStandardMaterial({
            color: 0xf5b041,
            roughness: 0.45,
            metalness: 0.25,
            emissive: 0x8a5a11,
            emissiveIntensity: 0.08
        });
        const doorMaterial = new THREE.MeshStandardMaterial({
            color: 0x1f2d3a,
            roughness: 0.5,
            metalness: 0.35
        });

        const addBox = (name, size, position, material, castShadow = true) => {
            const mesh = new THREE.Mesh(new THREE.BoxGeometry(size.x, size.y, size.z), material);
            mesh.name = name;
            mesh.position.set(position.x, position.y, position.z);
            mesh.castShadow = castShadow;
            mesh.receiveShadow = true;
            scene.add(mesh);
            return mesh;
        };

        // Murs principaux: fond, lateraux et facade avec ouverture centrale.
        addBox('garage-back-wall', { x: garageWidth, y: wallHeight, z: wallThickness }, { x: 0, y: wallHeight / 2, z: backZ }, wallMaterial);
        addBox('garage-left-wall', { x: wallThickness, y: wallHeight, z: garageDepth }, { x: -sideX, y: wallHeight / 2, z: 0 }, innerWallMaterial);
        addBox('garage-right-wall', { x: wallThickness, y: wallHeight, z: garageDepth }, { x: sideX, y: wallHeight / 2, z: 0 }, innerWallMaterial);
        addBox('garage-front-left-wall', { x: (garageWidth - doorWidth) / 2, y: wallHeight, z: wallThickness }, { x: -(garageWidth + doorWidth) / 4, y: wallHeight / 2, z: frontZ }, wallMaterial);
        addBox('garage-front-right-wall', { x: (garageWidth - doorWidth) / 2, y: wallHeight, z: wallThickness }, { x: (garageWidth + doorWidth) / 4, y: wallHeight / 2, z: frontZ }, wallMaterial);
        addBox('garage-front-top-wall', { x: doorWidth, y: wallHeight - doorHeight, z: wallThickness }, { x: 0, y: doorHeight + ((wallHeight - doorHeight) / 2), z: frontZ }, wallMaterial);

        // Porte d'entree style rideau metallique, legerement ouverte pour garder la lecture de l'interieur.
        const door = addBox('garage-entry-door', { x: doorWidth - 1.5, y: doorHeight * 0.72, z: 0.45 }, { x: 0, y: doorHeight * 0.36, z: frontZ + 0.7 }, doorMaterial);
        door.receiveShadow = false;
        const doorLines = new THREE.Group();
        for (let y = 1; y < doorHeight * 0.72; y += 1.1) {
            const line = addBox('garage-door-line', { x: doorWidth - 1.1, y: 0.08, z: 0.08 }, { x: 0, y, z: frontZ + 0.96 }, trimMaterial, false);
            doorLines.add(line);
        }
        scene.add(doorLines);
        addBox('garage-door-frame-top', { x: doorWidth + 2, y: 0.6, z: 1.1 }, { x: 0, y: doorHeight + 0.3, z: frontZ + 0.2 }, trimMaterial);
        addBox('garage-door-frame-left', { x: 0.6, y: doorHeight + 0.6, z: 1.1 }, { x: -doorWidth / 2 - 0.3, y: (doorHeight + 0.6) / 2, z: frontZ + 0.2 }, trimMaterial);
        addBox('garage-door-frame-right', { x: 0.6, y: doorHeight + 0.6, z: 1.1 }, { x: doorWidth / 2 + 0.3, y: (doorHeight + 0.6) / 2, z: frontZ + 0.2 }, trimMaterial);

        // Poutres hautes pour donner une vraie structure de batiment.
        [-30, -15, 0, 15, 30].forEach((z) => {
            addBox('garage-roof-beam', { x: garageWidth + 2, y: 0.45, z: 0.45 }, { x: 0, y: wallHeight + 0.35, z }, trimMaterial);
        });
        [-32, 0, 32].forEach((x) => {
            addBox('garage-depth-beam', { x: 0.45, y: 0.45, z: garageDepth + 2 }, { x, y: wallHeight + 0.8, z: 0 }, trimMaterial);
        });

        const zoneMaterial = (color) => new THREE.MeshBasicMaterial({
            color,
            transparent: true,
            opacity: 0.22,
            side: THREE.DoubleSide,
            depthWrite: false
        });
        const zoneBorderMaterial = new THREE.LineBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.55 });

        const addZone = ({ name, label, x, z, width, depth, color }) => {
            const zone = new THREE.Mesh(new THREE.PlaneGeometry(width, depth), zoneMaterial(color));
            zone.name = name;
            zone.rotation.x = -Math.PI / 2;
            zone.position.set(x, 0.035, z);
            scene.add(zone);

            const border = new THREE.LineSegments(new THREE.EdgesGeometry(new THREE.PlaneGeometry(width, depth)), zoneBorderMaterial);
            border.rotation.x = -Math.PI / 2;
            border.position.set(x, 0.055, z);
            scene.add(border);

            const sprite = createTextSprite(label, `#${color.toString(16).padStart(6, '0')}`);
            sprite.position.set(x, 1.2, z - depth / 2 + 2.5);
            sprite.scale.set(8, 2, 1);
            scene.add(sprite);
        };

        addZone({ name: 'storage-zone-active', label: 'Zone Active', x: -24, z: -10, width: 30, depth: 38, color: 0x2ecc71 });
        addZone({ name: 'storage-zone-reserve', label: 'Zone Reserve', x: 22, z: -10, width: 30, depth: 38, color: 0xf39c12 });
        addZone({ name: 'storage-zone-archive', label: 'Zone Archive', x: 0, z: 23, width: 42, depth: 17, color: 0x95a5a6 });

        // Allees principales et reperes de circulation.
        addBox('garage-central-aisle', { x: 8, y: 0.06, z: garageDepth - 8 }, { x: 0, y: 0.07, z: -2 }, new THREE.MeshStandardMaterial({
            color: 0x566573,
            roughness: 0.95,
            metalness: 0.05,
            transparent: true,
            opacity: 0.75
        }), false);
        addBox('garage-entry-marker', { x: 12, y: 0.08, z: 1.2 }, { x: 0, y: 0.1, z: frontZ - 8 }, trimMaterial, false);

        const laneMaterial = new THREE.MeshBasicMaterial({
            color: 0xf8f9fa,
            transparent: true,
            opacity: 0.55,
            depthWrite: false
        });
        [-12, 12].forEach(x => {
            addBox('garage-lane-line', { x: 0.18, y: 0.07, z: garageDepth - 12 }, { x, y: 0.14, z: -1 }, laneMaterial, false);
        });
        [-26, -14, -2, 10, 22].forEach(z => {
            addBox('garage-cross-lane-line', { x: garageWidth - 18, y: 0.07, z: 0.18 }, { x: 0, y: 0.145, z }, laneMaterial, false);
        });

        const slotBorderMaterial = new THREE.LineBasicMaterial({ color: 0xd7dde8, transparent: true, opacity: 0.38 });
        storageSlots.forEach(slot => {
            const border = new THREE.LineSegments(new THREE.EdgesGeometry(new THREE.PlaneGeometry(5.2, 5.2)), slotBorderMaterial);
            border.rotation.x = -Math.PI / 2;
            border.position.set(slot.x * 6, 0.12, slot.z * 6);
            border.name = `slot-reference-${slot.x}-${slot.z}`;
            scene.add(border);
        });

        for (let x = -5; x <= 5; x += 1) {
            const label = createTextSprite(`X ${x}`, '#34495e');
            label.position.set(x * 6, 0.85, frontZ - 4);
            label.scale.set(2.6, 0.65, 1);
            scene.add(label);
        }
        for (let z = -4; z <= 5; z += 1) {
            const label = createTextSprite(`Z ${z}`, '#34495e');
            label.position.set(-sideX + 4, 0.85, z * 6);
            label.scale.set(2.6, 0.65, 1);
            scene.add(label);
        }
    };

    useEffect(() => {
        initScene();
        fetchPalettes();

        const handleResize = () => {
            if (cameraRef.current && rendererRef.current && mountRef.current) {
                const container = mountRef.current;
                cameraRef.current.aspect = container.clientWidth / container.clientHeight;
                cameraRef.current.updateProjectionMatrix();
                rendererRef.current.setSize(container.clientWidth, container.clientHeight);
            }
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
            if (rendererRef.current) {
                // Nettoyer proprement le renderer
                const canvas = rendererRef.current.domElement;
                if (canvas && canvas.parentElement) {
                    canvas.parentElement.removeChild(canvas);
                }
                rendererRef.current.dispose();
                rendererRef.current = null;
            }
            if (sceneRef.current) {
                sceneRef.current.clear();
                sceneRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        if (sceneRef.current && palettes.length > 0) {
            updatePalettesInScene();
        }
    }, [palettes, filterStatus, showLabels]);

    useEffect(() => {
        movingPaletteRef.current = movingPalette;
        updateAvailablePositionMarkers();
    }, [movingPalette, palettes]);

    const initScene = () => {
        if (!mountRef.current) return;

        // Eviter le double rendu en verifiant si un canvas existe deja
        if (mountRef.current.querySelector('canvas')) {
            console.log('Canvas deja cree, skip re-creation');
            return;
        }

        const container = mountRef.current;
        const width = container.clientWidth;
        const height = container.clientHeight;

        console.log('Initialisation de la scene 3D...');

        // Scene avec couleur de fond BackOffice
        const scene = new THREE.Scene();
        scene.background = new THREE.Color(0x101827);
        scene.fog = new THREE.Fog(0x101827, 70, 145);
        sceneRef.current = scene;

        // Camera
        const camera = new THREE.PerspectiveCamera(55, width / height, 0.1, 1200);
        camera.position.set(26, 34, 44);
        camera.lookAt(0, 1.5, 0);
        cameraRef.current = camera;

        // Renderer
        const renderer = new THREE.WebGLRenderer({
            antialias: true,
            alpha: true
        });
        renderer.setSize(width, height);
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        renderer.shadowMap.enabled = true;
        renderer.shadowMap.type = THREE.PCFSoftShadowMap;
        renderer.outputColorSpace = THREE.SRGBColorSpace;
        renderer.toneMapping = THREE.ACESFilmicToneMapping;
        renderer.toneMappingExposure = 1.08;
        container.appendChild(renderer.domElement);

        // CRITIQUE: S'assurer que le canvas peut recevoir les evenements
        renderer.domElement.style.pointerEvents = 'auto';
        renderer.domElement.style.touchAction = 'none';
        renderer.domElement.style.cursor = 'grab';

        rendererRef.current = renderer;
        console.log('Renderer cree et ajoute au DOM');
        console.log('Taille canvas:', renderer.domElement.width, 'x', renderer.domElement.height);
        console.log('Style pointer-events du canvas:', window.getComputedStyle(renderer.domElement).pointerEvents);

        // Controles
        const controls = new OrbitControls(camera, renderer.domElement);
        controls.enableDamping = true;
        controls.dampingFactor = 0.08;
        controls.rotateSpeed = 0.62;
        controls.zoomSpeed = 0.9;
        controls.panSpeed = 0.72;
        controls.screenSpacePanning = false;
        controls.target.set(0, 1.4, 0);
        controls.maxPolarAngle = Math.PI / 2 - 0.06;
        controls.minPolarAngle = 0.22;
        controls.minDistance = 18;
        controls.maxDistance = 115;
        controls.enableZoom = true;
        controls.enableRotate = true;
        controls.enablePan = true;
        controlsRef.current = controls;
        console.log('OrbitControls initialises:', {
            zoom: controls.enableZoom,
            rotate: controls.enableRotate,
            pan: controls.enablePan,
            domElement: renderer.domElement.tagName
        });

        // TEST: Ajouter des event listeners de diagnostic
        const canvas = renderer.domElement;
        canvas.addEventListener('mousedown', (e) => {
            console.log('MOUSEDOWN sur canvas', e.button);
        });
        canvas.addEventListener('wheel', (e) => {
            console.log('WHEEL sur canvas', e.deltaY);
        });
        canvas.addEventListener('contextmenu', (e) => {
            console.log('CONTEXTMENU (clic droit)');
        });

        // Verifier que le canvas recoit bien les evenements
        console.log('Style du canvas:', {
            pointerEvents: window.getComputedStyle(canvas).pointerEvents,
            cursor: window.getComputedStyle(canvas).cursor,
            position: window.getComputedStyle(canvas).position
        });

        // Lumieres equilibrees pour un rendu plus lisible.
        const ambientLight = new THREE.AmbientLight(0xdde8ff, 0.42);
        scene.add(ambientLight);

        const hemiLight = new THREE.HemisphereLight(0xb7d8ff, 0x18202c, 0.58);
        scene.add(hemiLight);

        const directionalLight = new THREE.DirectionalLight(0xffffff, 1.25);
        directionalLight.position.set(-28, 42, 24);
        directionalLight.castShadow = true;
        directionalLight.shadow.mapSize.width = 4096;
        directionalLight.shadow.mapSize.height = 4096;
        directionalLight.shadow.camera.near = 0.5;
        directionalLight.shadow.camera.far = 140;
        directionalLight.shadow.camera.left = -65;
        directionalLight.shadow.camera.right = 65;
        directionalLight.shadow.camera.top = 58;
        directionalLight.shadow.camera.bottom = -58;
        directionalLight.shadow.bias = -0.00015;
        scene.add(directionalLight);

        const entrySpot = new THREE.SpotLight(0xfff2cf, 1.7, 105, Math.PI / 6, 0.35, 1.2);
        entrySpot.position.set(0, 18, 38);
        entrySpot.target.position.set(0, 0, 4);
        entrySpot.castShadow = true;
        entrySpot.shadow.mapSize.width = 2048;
        entrySpot.shadow.mapSize.height = 2048;
        scene.add(entrySpot);
        scene.add(entrySpot.target);

        const fillLight = new THREE.PointLight(0x6fa8ff, 0.8, 95);
        fillLight.position.set(30, 14, -28);
        scene.add(fillLight);

        // Sol du garage, dimensionne pour structurer les zones de stockage.
        const floorTexture = createCanvasTexture((ctx, canvas) => {
            ctx.fillStyle = '#242b3b';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.strokeStyle = 'rgba(255,255,255,0.045)';
            ctx.lineWidth = 2;
            for (let x = 0; x <= canvas.width; x += 64) {
                ctx.beginPath();
                ctx.moveTo(x, 0);
                ctx.lineTo(x, canvas.height);
                ctx.stroke();
            }
            for (let y = 0; y <= canvas.height; y += 64) {
                ctx.beginPath();
                ctx.moveTo(0, y);
                ctx.lineTo(canvas.width, y);
                ctx.stroke();
            }
            ctx.fillStyle = 'rgba(255,255,255,0.025)';
            for (let i = 0; i < 500; i++) {
                const x = Math.random() * canvas.width;
                const y = Math.random() * canvas.height;
                ctx.fillRect(x, y, 1.2, 1.2);
            }
        });
        floorTexture.repeat.set(14, 10);
        const floorGeometry = new THREE.PlaneGeometry(112, 86);
        const floorMaterial = new THREE.MeshStandardMaterial({
            color: 0x2a2a3e,
            map: floorTexture,
            roughness: 0.8,
            metalness: 0.2
        });
        const floor = new THREE.Mesh(floorGeometry, floorMaterial);
        floor.rotation.x = -Math.PI / 2;
        floor.receiveShadow = true;
        scene.add(floor);

        createGarageStructure(scene);

        // Grille de repere au sol, contenue dans le garage.
        const gridHelper = new THREE.GridHelper(96, 32, 0x667eea, 0x444466);
        gridHelper.position.y = 0.01;
        scene.add(gridHelper);

        // Raycaster pour la selection
        const raycaster = new THREE.Raycaster();
        const mouse = new THREE.Vector2();
        let mouseDownTime = 0;
        let mouseDownPos = { x: 0, y: 0 };

        // Detecter mousedown pour differencier click et drag
        renderer.domElement.addEventListener('mousedown', (event) => {
            mouseDownTime = Date.now();
            mouseDownPos = { x: event.clientX, y: event.clientY };
        });

        const onMouseClick = (event) => {
            // Si la souris a bouge de plus de 5px ou si plus de 200ms, c'est un drag, pas un click
            const timeDiff = Date.now() - mouseDownTime;
            const moveDist = Math.sqrt(
                Math.pow(event.clientX - mouseDownPos.x, 2) +
                Math.pow(event.clientY - mouseDownPos.y, 2)
            );

            if (timeDiff > 200 || moveDist > 5) {
                console.log('Drag detecte (pas un click), ignore');
                return; // C'est un drag d'OrbitControls, pas un click
            }

            console.log('Click detecte sur le canvas!');
            const rect = renderer.domElement.getBoundingClientRect();
            mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
            mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

            raycaster.setFromCamera(mouse, camera);

            if (movingPaletteRef.current) {
                const slotMeshes = positionMarkersRef.current.filter(obj => obj.isMesh && obj.userData.moveSlot);
                const slotIntersects = raycaster.intersectObjects(slotMeshes, true);
                if (slotIntersects.length > 0) {
                    const slot = slotIntersects[0].object.userData.moveSlot;
                    movePaletteToSlot(movingPaletteRef.current, slot);
                    return;
                }
            }

            const meshes = palettesRef.current.filter(obj => obj.isMesh);
            const intersects = raycaster.intersectObjects(meshes, true);

            // Chercher le premier intersect qui a userData.palette
            let paletteFound = null;
            for (let i = 0; i < intersects.length; i++) {
                const obj = intersects[i].object;
                if (obj.userData.palette) {
                    paletteFound = obj.userData.palette;
                    break;
                } else if (obj.parent && obj.parent.userData && obj.parent.userData.palette) {
                    paletteFound = obj.parent.userData.palette;
                    break;
                }
            }

            if (paletteFound) {
                console.log('Palette cliquee:', paletteFound.code);
                handlePaletteClick(paletteFound);
            } else {
                setSelectedPalette(null);
                movingPaletteRef.current = null;
                setMovingPalette(null);
                resetHighlights();
            }
        };

        renderer.domElement.addEventListener('click', onMouseClick);
        console.log('Event listener click ajoute au canvas');

        // Animation
        const animate = () => {
            animationFrameRef.current = requestAnimationFrame(animate);
            const elapsed = clockRef.current.getElapsedTime();

            if (cameraMoveRef.current && controlsRef.current) {
                camera.position.lerp(cameraMoveRef.current.position, 0.08);
                controlsRef.current.target.lerp(cameraMoveRef.current.target, 0.08);
                if (camera.position.distanceTo(cameraMoveRef.current.position) < 0.08) {
                    camera.position.copy(cameraMoveRef.current.position);
                    controlsRef.current.target.copy(cameraMoveRef.current.target);
                    cameraMoveRef.current = null;
                }
            }

            if (selectedHighlightRef.current) {
                const pulse = 1 + Math.sin(elapsed * 4.2) * 0.055;
                selectedHighlightRef.current.scale.set(pulse, 1, pulse);
                selectedHighlightRef.current.children.forEach(child => {
                    if (child.material && child.material.opacity !== undefined) {
                        child.material.opacity = 0.42 + Math.sin(elapsed * 4.2) * 0.14;
                    }
                });
            }

            positionMarkersRef.current.forEach(obj => {
                if (obj.material && obj.material.opacity !== undefined && obj.userData.moveSlot) {
                    obj.material.opacity = 0.34 + Math.sin(elapsed * 5) * 0.08;
                }
            });

            controls.update();
            renderer.render(scene, camera);
        };
        animate();
    };

    const fetchPalettes = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            // Recuperer TOUTES les palettes (limit eleve pour eviter pagination)
            const response = await axios.get(`${API_URL}/api/stock/items?limit=1000&order=import`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            console.log('Stock recupere:', response.data);
            console.log('Nombre total d\'items:', response.data.pagination?.total);

            // Grouper les items par palette (reference unique de la palette)
            const palettesMap = new Map();
            (response.data.data || []).forEach(item => {
                const paletteRef = item.palette || item.reference || 'N/A';

                if (!palettesMap.has(paletteRef)) {
                    palettesMap.set(paletteRef, {
                        _id: item._id,
                        code: paletteRef,
                        status: item.statusPalette || 'active',
                        position: item.positionPalette || { x: 0, y: 0, z: 0 },
                        items: []
                    });
                }

                palettesMap.get(paletteRef).items.push({
                    article: item.article,
                    reference: item.reference,
                    numeroSerie: item.numeroSerie,
                    quantite: item.quantite
                });
            });

            const palettesData = Array.from(palettesMap.values());
            console.log('Palettes uniques:', palettesData.length);
            console.log('Exemple de palette:', palettesData[0]);
            setPalettes(palettesData);
            updateStats(palettesData);
            setLoading(false);
        } catch (error) {
            console.error('Erreur lors de la recuperation des palettes:', error);
            setLoading(false);
            Swal.fire({
                icon: 'error',
                title: 'Erreur',
                text: 'Impossible de charger les palettes',
                background: '#2c3e50',
                color: '#ecf0f1'
            });
        }
    };

    const updateStats = (palettesData) => {
        setStats({
            total: palettesData.length,
            active: palettesData.filter(p => p.status === 'active').length,
            reserve: palettesData.filter(p => p.status === 'reserve').length,
            archive: palettesData.filter(p => p.status === 'archive').length
        });
    };

    const clearAvailablePositionMarkers = () => {
        if (!sceneRef.current) return;

        positionMarkersRef.current.forEach(obj => {
            if (obj.geometry) obj.geometry.dispose();
            if (obj.material) {
                if (Array.isArray(obj.material)) {
                    obj.material.forEach(mat => mat.dispose());
                } else {
                    obj.material.dispose();
                }
            }
            sceneRef.current.remove(obj);
        });
        positionMarkersRef.current = [];
    };

    const updateAvailablePositionMarkers = () => {
        clearAvailablePositionMarkers();

        const scene = sceneRef.current;
        const paletteToMove = movingPaletteRef.current;
        if (!scene || !paletteToMove) return;

        const slotLoads = palettes
            .map((p, index) => ({ palette: p, position: getEffectivePalettePosition(p, index) }))
            .filter(({ palette }) => palette._id !== paletteToMove._id)
            .reduce((loads, { position }) => {
                const key = positionKey(position);
                loads.set(key, (loads.get(key) || 0) + 1);
                return loads;
            }, new Map());

        const availableSlots = storageSlots
            .map(slot => ({ ...slot, load: slotLoads.get(positionKey(slot)) || 0 }))
            .filter(slot => slot.load < MAX_PALETTES_PER_SLOT);
        const slotColor = new THREE.Color(statusColors[paletteToMove.status] || '#3498db');
        const slotMaterial = new THREE.MeshBasicMaterial({
            color: slotColor,
            transparent: true,
            opacity: 0.42,
            side: THREE.DoubleSide,
            depthWrite: false
        });
        const edgeMaterial = new THREE.LineBasicMaterial({
            color: 0xffffff,
            transparent: true,
            opacity: 0.9
        });

        availableSlots.forEach(slot => {
            const slotMesh = new THREE.Mesh(new THREE.PlaneGeometry(5.2, 5.2), slotMaterial.clone());
            slotMesh.rotation.x = -Math.PI / 2;
            slotMesh.position.set(slot.x * 6, 0.16, slot.z * 6);
            slotMesh.userData.moveSlot = slot;
            slotMesh.name = `available-slot-${slot.x}-${slot.z}`;
            scene.add(slotMesh);
            positionMarkersRef.current.push(slotMesh);

            const border = new THREE.LineSegments(new THREE.EdgesGeometry(new THREE.PlaneGeometry(5.2, 5.2)), edgeMaterial.clone());
            border.rotation.x = -Math.PI / 2;
            border.position.set(slot.x * 6, 0.18, slot.z * 6);
            border.userData.moveSlot = slot;
            scene.add(border);
            positionMarkersRef.current.push(border);

            const capacityLabel = createTextSprite(`${slot.load}/${MAX_PALETTES_PER_SLOT}`, slot.load === 0 ? '#2ecc71' : '#f5b041');
            capacityLabel.position.set(slot.x * 6, 0.9, slot.z * 6);
            capacityLabel.scale.set(2.4, 0.65, 1);
            scene.add(capacityLabel);
            positionMarkersRef.current.push(capacityLabel);
        });
    };

    const createSolarPanelPalette = (palette, color) => {
        const group = new THREE.Group();
        group.userData.palette = palette;

        const woodMaterial = new THREE.MeshStandardMaterial({
            color: 0x9b6b3f,
            roughness: 0.78,
            metalness: 0.08
        });
        const darkWoodMaterial = new THREE.MeshStandardMaterial({
            color: 0x6f4728,
            roughness: 0.85,
            metalness: 0.05
        });
        const frameMaterial = new THREE.MeshStandardMaterial({
            color: 0xbfc7d5,
            roughness: 0.32,
            metalness: 0.75
        });
        const glassMaterial = new THREE.MeshPhysicalMaterial({
            color: 0x0b1f35,
            roughness: 0.16,
            metalness: 0.25,
            clearcoat: 0.8,
            clearcoatRoughness: 0.18,
            emissive: color,
            emissiveIntensity: 0.12
        });
        const cellMaterial = new THREE.MeshStandardMaterial({
            color: 0x162f4f,
            roughness: 0.28,
            metalness: 0.35,
            emissive: 0x07111f,
            emissiveIntensity: 0.2
        });
        const strapMaterial = new THREE.MeshStandardMaterial({
            color: 0x1b2631,
            roughness: 0.5,
            metalness: 0.45
        });

        const addMesh = (geometry, material, position, name) => {
            const mesh = new THREE.Mesh(geometry, material);
            mesh.name = name;
            mesh.position.set(position.x, position.y, position.z);
            mesh.castShadow = true;
            mesh.receiveShadow = true;
            mesh.userData.palette = palette;
            group.add(mesh);
            return mesh;
        };

        [-1.55, 0, 1.55].forEach(z => {
            addMesh(new THREE.BoxGeometry(5.2, 0.32, 0.32), woodMaterial, { x: 0, y: 0.18, z }, 'wood-runner');
        });
        [-1.9, 0, 1.9].forEach(x => {
            addMesh(new THREE.BoxGeometry(0.42, 0.72, 0.46), darkWoodMaterial, { x, y: 0.48, z: -1.55 }, 'wood-block');
            addMesh(new THREE.BoxGeometry(0.42, 0.72, 0.46), darkWoodMaterial, { x, y: 0.48, z: 0 }, 'wood-block');
            addMesh(new THREE.BoxGeometry(0.42, 0.72, 0.46), darkWoodMaterial, { x, y: 0.48, z: 1.55 }, 'wood-block');
        });
        [-1.6, 0, 1.6].forEach(x => {
            addMesh(new THREE.BoxGeometry(0.32, 0.18, 4.3), woodMaterial, { x, y: 0.92, z: 0 }, 'top-slat');
        });

        const panelCount = Math.min(5, Math.max(2, Math.ceil((palette.items?.length || 1) / 2)));
        for (let i = 0; i < panelCount; i++) {
            const y = 1.18 + (i * 0.23);
            addMesh(new THREE.BoxGeometry(4.7, 0.1, 3.15), frameMaterial, { x: 0, y, z: 0 }, 'solar-panel-frame');
            addMesh(new THREE.BoxGeometry(4.28, 0.08, 2.72), glassMaterial, { x: 0, y: y + 0.055, z: 0 }, 'solar-panel-glass');

            for (let col = -1; col <= 1; col++) {
                for (let row = -1; row <= 1; row++) {
                    addMesh(
                        new THREE.BoxGeometry(1.18, 0.022, 0.72),
                        cellMaterial,
                        { x: col * 1.32, y: y + 0.11, z: row * 0.86 },
                        'solar-cell'
                    );
                }
            }
        }

        addMesh(new THREE.BoxGeometry(0.16, 1.7, 4.6), strapMaterial, { x: -2.4, y: 1.54, z: 0 }, 'left-metal-strap');
        addMesh(new THREE.BoxGeometry(0.16, 1.7, 4.6), strapMaterial, { x: 2.4, y: 1.54, z: 0 }, 'right-metal-strap');
        addMesh(new THREE.BoxGeometry(5.2, 0.12, 0.18), strapMaterial, { x: 0, y: 1.98, z: -1.82 }, 'front-metal-strap');
        addMesh(new THREE.BoxGeometry(5.2, 0.12, 0.18), strapMaterial, { x: 0, y: 1.98, z: 1.82 }, 'back-metal-strap');

        const baseHighlight = new THREE.Mesh(
            new THREE.BoxGeometry(5.5, 0.12, 4.7),
            new THREE.MeshBasicMaterial({
                color,
                transparent: true,
                opacity: 0.16,
                depthWrite: false
            })
        );
        baseHighlight.position.set(0, 0.08, 0);
        baseHighlight.userData.palette = palette;
        group.add(baseHighlight);

        group.traverse(obj => {
            if (obj.isMesh) obj.userData.palette = palette;
        });

        return group;
    };

    const updatePalettesInScene = () => {
        if (!sceneRef.current) {
            console.log('Scene 3D non initialisee');
            return;
        }

        const scene = sceneRef.current;

        console.log('Mise a jour de la scene 3D...');
        console.log('Nombre de palettes a afficher:', palettes.length);

        // Supprimer les anciennes palettes et labels
        palettesRef.current.forEach(obj => {
            if (obj.geometry) obj.geometry.dispose();
            if (obj.material) {
                if (Array.isArray(obj.material)) {
                    obj.material.forEach(mat => mat.dispose());
                } else {
                    obj.material.dispose();
                }
            }
            scene.remove(obj);
        });
        palettesRef.current = [];

        // Filtrer les palettes
        let filteredPalettes = palettes;
        if (filterStatus !== 'all') {
            filteredPalettes = palettes.filter(p => p.status === filterStatus);
        }

        console.log('Palettes apres filtrage:', filteredPalettes.length);
        console.log('Statut filtre actuel:', filterStatus);

        if (filteredPalettes.length === 0) {
            console.log('Aucune palette a afficher!');
            return;
        }

        // Grouper les palettes par position pour empiler si necessaire
        const positionMap = new Map();

        // Calculer la taille de la grille en fonction du nombre de palettes
        const gridSize = Math.ceil(Math.sqrt(filteredPalettes.length)); // Grille carree
        console.log(`Taille de la grille: ${gridSize}x${gridSize}`);

        filteredPalettes.forEach((palette, index) => {
            const pos = getEffectivePalettePosition(palette, index);

            // Si toutes les positions sont 0,0,0, les repartir automatiquement
            let finalPos = pos;
            if (pos.x === 0 && pos.y === 0 && pos.z === 0) {
                // Repartition en grille dynamique (carree) - centree a l'origine
                const row = Math.floor(index / gridSize);
                const col = index % gridSize;
                // Centrer autour de (0,0) avec espacement de 1 unite
                finalPos = {
                    x: col - Math.floor(gridSize / 2),
                    y: 0,
                    z: row - Math.floor(gridSize / 2)
                };
                if (index < 5) {
                    console.log(`Palette ${palette.code} repartie en grille: row=${row}, col=${col}, x=${finalPos.x}, z=${finalPos.z}`);
                }
            }

            const key = `${finalPos.x},${finalPos.y},${finalPos.z}`;
            if (!positionMap.has(key)) {
                positionMap.set(key, []);
            }
            positionMap.get(key).push({ ...palette, position: finalPos });
        });

        console.log('Positions uniques:', positionMap.size);

        // Creer les meshes pour chaque palette
        let meshCount = 0;
        positionMap.forEach((palettesAtPos, posKey) => {
            palettesAtPos.forEach((palette, stackIndex) => {
                // Palettes GRANDES et bien visibles
                const geometry = new THREE.BoxGeometry(4, 3, 4);
                const color = new THREE.Color(statusColors[palette.status] || '#3498db');
                const material = new THREE.MeshStandardMaterial({
                    color: color,
                    roughness: 0.5,
                    metalness: 0.3,
                    emissive: color,
                    emissiveIntensity: 0.5, // Augmente pour etre plus visible
                    transparent: true,
                    opacity: 0.08,
                    visible: true,
                    side: THREE.DoubleSide
                });

                const mesh = new THREE.Mesh(geometry, material);
                material.depthWrite = false;
                mesh.visible = true;
                mesh.frustumCulled = false; // Desactiver le frustum culling temporairement
                const pos = palette.position;

                // Empiler verticalement si plusieurs palettes a la meme position
                const heightOffset = stackIndex * 3.5;
                // Espacement de 6 unites entre les palettes pour visibilite
                mesh.position.set(
                    pos.x * 6,
                    1.5 + heightOffset,
                    pos.z * 6
                );

                mesh.castShadow = true;
                mesh.receiveShadow = true;
                mesh.userData.palette = palette;

                // Ajouter un contour
                const edges = new THREE.EdgesGeometry(geometry);
                const lineMaterial = new THREE.LineBasicMaterial({ color: 0x2c3e50, linewidth: 2 });
                const wireframe = new THREE.LineSegments(edges, lineMaterial);
                wireframe.raycast = () => { }; // Desactiver le raycasting sur les wireframes
                mesh.add(wireframe);

                const realisticPalette = createSolarPanelPalette(palette, color);
                realisticPalette.position.set(mesh.position.x, heightOffset, mesh.position.z);
                scene.add(realisticPalette);
                palettesRef.current.push(realisticPalette);
                realisticPalette.traverse(obj => {
                    if (obj.isMesh) {
                        obj.frustumCulled = false;
                        palettesRef.current.push(obj);
                    }
                });

                scene.add(mesh);
                palettesRef.current.push(mesh);
                meshCount++;

                // Ajouter un label
                if (showLabels) {
                    const sprite = createTextSprite(palette.code, statusColors[palette.status]);
                    sprite.position.set(
                        mesh.position.x,
                        mesh.position.y + 3,
                        mesh.position.z
                    );
                    scene.add(sprite);
                    palettesRef.current.push(sprite);
                }

                if (meshCount < 5) {
                    console.log(`Palette ${palette.code} creee:`);
                    console.log(`   - Position grille: (${pos.x}, ${pos.y}, ${pos.z})`);
                    console.log(`   - Position 3D finale: (${mesh.position.x.toFixed(1)}, ${mesh.position.y.toFixed(1)}, ${mesh.position.z.toFixed(1)})`);
                    console.log(`   - Visible: ${mesh.visible}, Material visible: ${mesh.material.visible}`);
                    console.log(`   - Couleur: ${mesh.material.color.getHexString()}, Emissive: ${mesh.material.emissive.getHexString()}`);
                }
            });
        });

        console.log('Total meshes 3D crees:', meshCount);
        console.log('Total objets dans palettesRef:', palettesRef.current.length);
        console.log('Camera position:', cameraRef.current.position);
        console.log('Camera regarde vers:', controlsRef.current.target);
    };

    const createTextSprite = (text, color) => {
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.width = 256;
        canvas.height = 64;

        // Fond avec la couleur du statut
        context.fillStyle = color;
        context.fillRect(0, 0, canvas.width, canvas.height);

        // Bordure
        context.strokeStyle = '#2c3e50';
        context.lineWidth = 4;
        context.strokeRect(0, 0, canvas.width, canvas.height);

        // Texte
        context.fillStyle = '#ffffff';
        context.font = 'Bold 28px Arial';
        context.textAlign = 'center';
        context.textBaseline = 'middle';
        context.fillText(text, canvas.width / 2, canvas.height / 2);

        const texture = new THREE.CanvasTexture(canvas);
        const material = new THREE.SpriteMaterial({
            map: texture,
            transparent: true
        });
        const sprite = new THREE.Sprite(material);
        sprite.scale.set(4, 1, 1);

        return sprite;
    };

    const handlePaletteClick = (palette) => {
        console.log('Palette cliquee:', palette);
        movingPaletteRef.current = null;
        setMovingPalette(null);
        setSelectedPalette(palette);
        highlightPalette(palette);
    };

    const clearSelectionHighlight = () => {
        if (!selectedHighlightRef.current || !sceneRef.current) return;
        sceneRef.current.remove(selectedHighlightRef.current);
        disposeObject(selectedHighlightRef.current);
        selectedHighlightRef.current = null;
    };

    const highlightPalette = (palette) => {
        resetHighlights();

        const mesh = palettesRef.current.find(m =>
            m.isMesh && m.userData.palette && m.userData.palette._id === palette._id
        );

        if (mesh && mesh.material) {
            mesh.material.emissiveIntensity = 0.5;
        }

        if (!mesh || !sceneRef.current) return;

        clearSelectionHighlight();

        const highlight = new THREE.Group();
        highlight.name = 'selected-palette-highlight';
        highlight.position.set(mesh.position.x, 0.12, mesh.position.z);

        const ringMaterial = new THREE.MeshBasicMaterial({
            color: 0xf5b041,
            transparent: true,
            opacity: 0.48,
            side: THREE.DoubleSide,
            depthWrite: false
        });
        const ring = new THREE.Mesh(new THREE.RingGeometry(3.6, 4.5, 72), ringMaterial);
        ring.rotation.x = -Math.PI / 2;
        highlight.add(ring);

        const cornerMaterial = new THREE.MeshBasicMaterial({
            color: 0xf8f9fa,
            transparent: true,
            opacity: 0.72,
            depthWrite: false
        });
        [
            [-2.8, -2.35], [2.8, -2.35], [-2.8, 2.35], [2.8, 2.35]
        ].forEach(([x, z]) => {
            const corner = new THREE.Mesh(new THREE.BoxGeometry(0.18, 3.6, 0.18), cornerMaterial.clone());
            corner.position.set(x, 1.8, z);
            highlight.add(corner);
        });

        sceneRef.current.add(highlight);
        selectedHighlightRef.current = highlight;
    };

    const resetHighlights = () => {
        clearSelectionHighlight();
        palettesRef.current.forEach(mesh => {
            if (mesh.isMesh && mesh.material) {
                mesh.material.emissiveIntensity = 0.1;
            }
        });
    };

    const changeView = (mode) => {
        setViewMode(mode);
        const camera = cameraRef.current;
        const controls = controlsRef.current;
        if (!camera || !controls) return;

        const moveCamera = (position, target = new THREE.Vector3(0, 1.4, 0)) => {
            cameraMoveRef.current = {
                position,
                target
            };
        };

        switch (mode) {
            case 'TOP':
                moveCamera(new THREE.Vector3(0, 86, 0.1), new THREE.Vector3(0, 0, 0));
                break;
            case 'FRONT':
                moveCamera(new THREE.Vector3(0, 18, 62), new THREE.Vector3(0, 2, 0));
                break;
            case 'SIDE':
                moveCamera(new THREE.Vector3(64, 20, 0), new THREE.Vector3(0, 2, 0));
                break;
            case '3D':
            default:
                moveCamera(new THREE.Vector3(26, 34, 44), new THREE.Vector3(0, 1.4, 0));
                break;
        }
    };

    const movePaletteToSlot = async (palette, slot) => {
        if (!palette || !slot) return;

        try {
            const token = localStorage.getItem('token');
            await axios.patch(
                `${API_URL}/api/stock/items/${palette._id}/position`,
                { x: slot.x, y: slot.y, z: slot.z },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            Swal.fire({
                icon: 'success',
                title: 'Succes',
                text: `Palette deplacee vers la position X:${slot.x}, Z:${slot.z} (${slot.load + 1}/${MAX_PALETTES_PER_SLOT})`,
                timer: 1400,
                showConfirmButton: false,
                background: '#2c3e50',
                color: '#ecf0f1'
            });

            clearAvailablePositionMarkers();
            setMovingPalette(null);
            setSelectedPalette(null);
            cameraMoveRef.current = {
                position: new THREE.Vector3(slot.x * 6 + 14, 18, slot.z * 6 + 18),
                target: new THREE.Vector3(slot.x * 6, 1.8, slot.z * 6)
            };
            fetchPalettes();
        } catch (error) {
            console.error('Erreur lors du deplacement:', error);
            Swal.fire({
                icon: 'error',
                title: 'Erreur',
                text: 'Impossible de deplacer la palette',
                background: '#2c3e50',
                color: '#ecf0f1'
            });
        }
    };

    const startPaletteMoveMode = () => {
        if (!selectedPalette) return;

        movingPaletteRef.current = selectedPalette;
        setMovingPalette(selectedPalette);
        updateAvailablePositionMarkers();

        Swal.fire({
            icon: 'info',
            title: 'Choisissez une case disponible',
            text: `Cliquez sur une case coloree. Une meme case peut contenir jusqu'a ${MAX_PALETTES_PER_SLOT} palettes.`,
            timer: 1800,
            showConfirmButton: false,
            background: '#2c3e50',
            color: '#ecf0f1'
        });
    };

    return (
        <div className="wh3d-warehouse-container">
            <BackOfficeMenu
                onNavigate={onNavigate}
                onLogout={onLogout}
                currentPage={currentPage}
            />

            <div className="wh3d-warehouse-content">
                <div className="wh3d-warehouse-header">
                    <div className="wh3d-header-left">
                        <h1>Garage Virtuel 3D</h1>
                        <p>Visualisation et gestion des positions de palettes</p>
                    </div>
                    <button className="wh3d-btn-refresh" onClick={fetchPalettes}>
                        Actualiser
                    </button>
                </div>

                {/* Stats Bar */}
                <div className="wh3d-stats-bar">
                    <div className="wh3d-stat-card total">
                        <div className="wh3d-stat-value">{stats.total}</div>
                        <div className="wh3d-stat-label">Total Palettes</div>
                    </div>
                    <div className="wh3d-stat-card active">
                        <div className="wh3d-stat-value">{stats.active}</div>
                        <div className="wh3d-stat-label">Active</div>
                    </div>
                    <div className="wh3d-stat-card reserve">
                        <div className="wh3d-stat-value">{stats.reserve}</div>
                        <div className="wh3d-stat-label">Reserve</div>
                    </div>
                    <div className="wh3d-stat-card archive">
                        <div className="wh3d-stat-value">{stats.archive}</div>
                        <div className="wh3d-stat-label">Archive</div>
                    </div>
                </div>

                {/* Controls */}
                <div className="wh3d-controls-panel">
                    <div className="wh3d-control-group">
                        <label>Vue:</label>
                        <div className="wh3d-view-buttons">
                            <button
                                className={`wh3d-btn-view ${viewMode === '3D' ? 'active' : ''}`}
                                onClick={() => changeView('3D')}
                            >
                                3D
                            </button>
                            <button
                                className={`wh3d-btn-view ${viewMode === 'TOP' ? 'active' : ''}`}
                                onClick={() => changeView('TOP')}
                            >
                                Dessus
                            </button>
                            <button
                                className={`wh3d-btn-view ${viewMode === 'FRONT' ? 'active' : ''}`}
                                onClick={() => changeView('FRONT')}
                            >
                                Face
                            </button>
                            <button
                                className={`wh3d-btn-view ${viewMode === 'SIDE' ? 'active' : ''}`}
                                onClick={() => changeView('SIDE')}
                            >
                                Cote
                            </button>
                        </div>
                    </div>

                    <div className="wh3d-control-group">
                        <label>Filtre:</label>
                        <select
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                            className="wh3d-filter-select"
                        >
                            <option value="all">Tous</option>
                            <option value="active">Active</option>
                            <option value="reserve">Reserve</option>
                            <option value="archive">Archive</option>
                        </select>
                    </div>

                    <div className="wh3d-control-group">
                        <label className="wh3d-checkbox-label">
                            <input
                                type="checkbox"
                                checked={showLabels}
                                onChange={(e) => setShowLabels(e.target.checked)}
                            />
                            Afficher les labels
                        </label>
                    </div>
                </div>

                {/* Canvas 3D */}
                <div className="wh3d-canvas-container" ref={mountRef}>
                    {loading && (
                        <div className="wh3d-loading-overlay">
                            <LoadingSpinner message="Chargement des palettes..." />
                        </div>
                    )}
                    <div className="wh3d-navigation-hud">
                        <span>Rotation: clic gauche</span>
                        <span>Zoom: molette</span>
                        <span>Deplacement: clic droit</span>
                    </div>
                    {movingPalette && (
                        <div className="wh3d-move-mode-banner">
                            <span>Selectionnez une case libre pour {movingPalette.code}</span>
                            <button
                                type="button"
                                onClick={() => {
                                    movingPaletteRef.current = null;
                                    setMovingPalette(null);
                                    clearAvailablePositionMarkers();
                                }}
                            >
                                Annuler
                            </button>
                        </div>
                    )}
                </div>

                {/* Legende */}
                <div className="wh3d-legend">
                    <h4>Legende</h4>
                    <div className="wh3d-legend-item">
                        <div className="wh3d-legend-color" style={{ backgroundColor: '#2ecc71' }}></div>
                        <span>Active</span>
                    </div>
                    <div className="wh3d-legend-item">
                        <div className="wh3d-legend-color" style={{ backgroundColor: '#f39c12' }}></div>
                        <span>Reserve</span>
                    </div>
                    <div className="wh3d-legend-item">
                        <div className="wh3d-legend-color" style={{ backgroundColor: '#95a5a6' }}></div>
                        <span>Archive</span>
                    </div>
                </div>

                {/* Info Panel */}
                {selectedPalette && (
                    <div className="wh3d-info-panel">
                        <h3>Informations Palette</h3>
                        <div className="wh3d-info-content">
                            <div className="wh3d-info-row">
                                <span className="wh3d-info-label">Reference:</span>
                                <span className="wh3d-info-value">{selectedPalette.code}</span>
                            </div>
                            <div className="wh3d-info-row">
                                <span className="wh3d-info-label">Statut:</span>
                                <span className={`wh3d-status-badge ${selectedPalette.status}`}>
                                    {selectedPalette.status}
                                </span>
                            </div>
                            <div className="wh3d-info-row">
                                <span className="wh3d-info-label">Position:</span>
                                <span className="wh3d-info-value">
                                    X: {selectedPalette.position.x},
                                    Y: {selectedPalette.position.y},
                                    Z: {selectedPalette.position.z}
                                </span>
                            </div>
                            <div className="wh3d-info-row">
                                <span className="wh3d-info-label">Produits:</span>
                                <span className="wh3d-info-value">{selectedPalette.items?.length || 0}</span>
                            </div>
                            {selectedPalette.items && selectedPalette.items.length > 0 && (
                                <div style={{ marginTop: '1rem', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '1rem' }}>
                                    <strong style={{ color: 'rgba(255,255,255,0.7)' }}>Liste des produits:</strong>
                                    <div style={{ maxHeight: '200px', overflowY: 'auto', marginTop: '0.5rem' }}>
                                        {selectedPalette.items.map((item, idx) => (
                                            <div key={idx} style={{
                                                padding: '0.5rem',
                                                background: 'rgba(255,255,255,0.05)',
                                                borderRadius: '4px',
                                                marginBottom: '0.5rem',
                                                fontSize: '0.85rem'
                                            }}>
                                                {item.article && <div><strong>Article:</strong> {item.article}</div>}
                                                {item.numeroSerie && <div><strong>No Serie:</strong> {item.numeroSerie}</div>}
                                                {item.reference && <div><strong>Ref:</strong> {item.reference}</div>}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="wh3d-info-actions">
                            {hasPermission('warehouse', 'update') && (
                                <button className="wh3d-btn-move" onClick={startPaletteMoveMode}>
                                    Deplacer
                                </button>
                            )}
                            <button className="wh3d-btn-close" onClick={() => setSelectedPalette(null)}>
                                Fermer
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default WarehouseVisualization3D;
